import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, FormControlLabel, InputLabel, Link } from '@material-ui/core'
import { Loader } from '@refera/ui-web'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { navigate } from '@reach/router'
import { Checkbox } from '@mui/material'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'

import HeaderTitle from '_/components/header-title'
import { BasicInput, MaskBasicInput, Select } from '_/components/inputs'
import { ControlledCpfCnpjInput } from '_/components/inputs/MaskInput/common/CpfCnpjInput'
import { ROUTES, CPF_CNPJ_REGEX } from '_/utils/constants'
import { validationCep, cpfCnpjMask, phoneNumberMask } from '_/utils/helpers'
import NavbarButtons from '_views/agency-user/navbar-buttons'
import { AddCity } from '_views/agency-user/add-city'
import { Attachments } from '_/components/refera-ui'
import {
  createInspectionTradesman,
  getInspectionTradesmanById,
  updateInspectionTradesman,
} from '_/modules/inspectionBudget/actions'
import { getInspectionTradesmen as getInspectionTradesmenService } from '_/services/inspection-budget'
import { getCities, getStates } from '_/modules/utils/actions'
import { getCitiesOptions, getStatesOptions } from '_/modules/utils/selectors'
import useCepAutoComplete from '_/hooks/use-cep-autocomplete'
import { useToast } from '_/hooks/use-toast'

import useStyles from './styles'

export default function NewProvider({ providerId }) {
  const styles = useStyles()
  const dispatch = useDispatch()

  const citiesOptions = useSelector(getCitiesOptions)
  const stateOptions = useSelector(getStatesOptions)
  const authToken = useSelector(state => state.user.authToken)

  useEffect(() => {
    dispatch(getStates())
    dispatch(getCities())
  }, [dispatch])

  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      name: '',
      cpfCnpj: '',
      phone: '',
      cep: '',
      address: '',
      number: '',
      complement: '',
      state: '',
      city: '',
      neighborhood: '',
      isActive: true,
      cities: [],
    },
  })
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods
  const { showToast } = useToast()

  const stateSelected = watch('state')
  const cep = watch('cep')
  const cpfCnpjValue = watch('cpfCnpj')
  const cepData = useCepAutoComplete(cep, true)
  const [provider, setProvider] = useState({})

  const handleCpfCnpjBlur = React.useCallback(async () => {
    const onlyNumbers = cpfCnpjValue?.replace(/\D/g, '')
    if (!onlyNumbers || onlyNumbers.length < 11) return

    try {
      const data = await getInspectionTradesmenService(authToken)({ cpf_cnpj: onlyNumbers })
      const exists = data?.results?.some(item => {
        const itemDoc = item.cpfCnpj?.replace(/\D/g, '')
        return itemDoc === onlyNumbers && String(item.id) !== String(providerId)
      })

      if (exists) {
        setError('cpfCnpj', {
          type: 'manual',
          message: 'CPF/CNPJ já cadastrado',
        })
      } else {
        clearErrors('cpfCnpj')
      }
    } catch (err) {
      showToast({ type: 'error', message: 'Erro ao validar CPF/CNPJ' })
    }
  }, [cpfCnpjValue, authToken, providerId, showToast, setError, clearErrors])

  const cityOptions = useMemo(() => {
    if (!stateSelected) return []
    return citiesOptions.filter(c => c.state.uf === stateSelected)
  }, [citiesOptions, stateSelected])

  useEffect(() => {
    if (cepData?.address) {
      setValue('state', cepData.state)
      setValue('address', cepData.address)
      setValue('neighborhood', cepData.neighborhood)
      const findCity = cityOptions.find(city => city?.label === cepData?.city)
      if (findCity) setValue('city', findCity.label)
    }
  }, [cepData, cityOptions, setValue])

  const [isEditing, setIsEditing] = useState(!providerId)
  const [isLoading, setIsLoading] = useState(false)
  const [logo, setLogo] = useState([])

  useEffect(() => {
    if (providerId) {
      setIsLoading(true)
      dispatch(getInspectionTradesmanById(providerId))
        .then(data => {
          setProvider(data || {})
          reset({
            name: data.name,
            cpfCnpj: cpfCnpjMask(data.cpfCnpj),
            phone: phoneNumberMask(data.phone),
            cep: data.cep,
            address: data.address,
            number: data.number,
            complement: data.complement,
            state: data.state,
            city: data.city,
            neighborhood: data.neighborhood,
            isActive: data.isActive,
            cities: Array.isArray(data?.cities) ? data.cities : [],
          })
          if (data.logo) {
            setLogo([{ name: 'Logo', file: data.logo }])
          }
        })
        .catch(() => {
          showToast({ type: 'error', message: 'Erro ao buscar dados do prestador' })
          navigate(ROUTES.INSPECTION_BUDGET_PROVIDERS)
        })
        .finally(() => setIsLoading(false))
    }
  }, [providerId, dispatch, reset, showToast])

  const normalizeFilename = filename => {
    const parts = filename.split('.')
    const ext = parts.length > 1 ? parts.pop() : ''
    const name = parts.join('.').replace(/[^a-zA-Z0-9]/g, '')
    return ext ? `${name}.${ext}` : name
  }

  const onSubmit = async data => {
    setIsLoading(true)
    try {
      const logoFile = logo?.[0] instanceof File ? logo[0] : logo?.[0]?.file
      const finalLogo =
        logoFile instanceof File || logoFile instanceof Blob
          ? new File([logoFile], normalizeFilename(logoFile.name || 'logo.png'), {
              type: logoFile.type || 'image/png',
              lastModified: logoFile.lastModified || new Date().getTime(),
            })
          : undefined

      const payload = {
        name: data.name,
        cpfCnpj: data.cpfCnpj?.replace(/\D/g, '') ?? '',
        phone: data.phone?.replace(/\D/g, '') ?? '',
        cep: data.cep.replace('.', ''),
        address: data.address,
        number: data.number,
        complement: data.complement,
        state: data.state,
        city: data.city,
        neighborhood: data.neighborhood,
        isActive: data.isActive,
        cities: Array.isArray(data?.cities) ? data.cities : [],
        logo: finalLogo,
      }

      if (providerId) {
        await dispatch(updateInspectionTradesman(providerId, payload))
        showToast({ type: 'success', message: 'Prestador atualizado com sucesso' })
      } else {
        await dispatch(createInspectionTradesman(payload))
        showToast({ type: 'success', message: 'Prestador criado com sucesso' })
      }
      navigate(ROUTES.INSPECTION_BUDGET_PROVIDERS)
    } catch (error) {
      const action = providerId ? 'atualizar' : 'criar'
      const errorMessage = error?.message || `Erro ao ${action} prestador`
      showToast({ type: 'error', message: errorMessage })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Grid className={styles.container}>
      <Loader hasBackdrop open={isLoading} />
      <HeaderTitle
        title={providerId ? 'Editar Prestador' : 'Novo Prestador'}
        backButtonAction={() => navigate(ROUTES.INSPECTION_BUDGET_PROVIDERS)}
      >
        <NavbarButtons
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          isLoading={isLoading}
          user={{}}
          hideResend
          hasChanges
          formId="new-provider-form"
          onCancel={() => navigate(ROUTES.INSPECTION_BUDGET_PROVIDERS)}
        />
      </HeaderTitle>

      <FormProvider {...methods}>
        <form id="new-provider-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid className={styles.main}>
            {/* Dados principais */}
            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados principais</InputLabel>
              <div className={styles.wrapper}>
                <BasicInput
                  label="Nome completo"
                  name="name"
                  required
                  labelClasses={styles.inputLabel}
                  showErrorMessage={!!errors?.name?.message}
                  customErrorMessage={errors?.name?.message}
                  rules={{
                    required: 'Esse campo é obrigatório',
                  }}
                  disabled={!isEditing}
                />

                <ControlledCpfCnpjInput
                  label="CPF/CNPJ"
                  name="cpfCnpj"
                  labelProps={{
                    className: styles.inputLabel,
                    style: { marginTop: '-10px', paddingTop: '0px' },
                  }}
                  required
                  errorMessage={errors?.cpfCnpj?.message}
                  showErrorMessage={!!errors?.cpfCnpj?.message}
                  fieldProps={{
                    disabled: !isEditing,
                    onBlur: handleCpfCnpjBlur,
                  }}
                  controllerProps={{
                    rules: {
                      required: 'Esse campo é obrigatório',
                      pattern: { value: CPF_CNPJ_REGEX, message: 'CPF ou CNPJ inválido' },
                    },
                  }}
                />

                <MaskBasicInput
                  label="Telefone"
                  name="phone"
                  mask="+55 (99) 9 9999-9999"
                  required
                  disabled={!isEditing}
                  labelClasses={styles.inputLabel}
                  rules={{
                    required: { value: true, message: 'Esse campo é obrigatório' },
                    minLength: { value: 19, message: 'Telefone incompleto' },
                  }}
                />

                <div className={styles.checkboxField}>
                  <Controller
                    name="isActive"
                    control={control}
                    defaultValue
                    render={({ onChange, value }) => (
                      <FormControlLabel
                        className={styles.checkbox}
                        control={
                          <Checkbox
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                            color="primary"
                            disabled={!isEditing}
                          />
                        }
                        label="Ativo"
                      />
                    )}
                  />
                </div>
              </div>
              <Attachments
                label="Logo"
                files={logo || []}
                accept={{
                  'image/jpeg': ['.jpeg', '.png', '.jpg', '.bmp'],
                }}
                multiple={false}
                onDrop={files => setLogo(files)}
                onRemoveItem={() => setLogo([])}
                onItemClick={index => {
                  const file = logo[index]?.file
                  if (file instanceof File) {
                    const url = URL.createObjectURL(file)
                    const win = window.open(url)
                    if (win) {
                      win.onload = () => URL.revokeObjectURL(url)
                    }
                  } else {
                    window.open(file)
                  }
                }}
                readOnly={!isEditing}
              />
            </div>

            {/* Endereço */}
            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Endereço</InputLabel>
              <div className={styles.wrapper}>
                <div>
                  <MaskBasicInput
                    label="CEP"
                    name="cep"
                    mask="99.999-999"
                    required
                    disabled={!isEditing}
                    rules={{
                      validate: value => !validationCep(value) || 'CEP inválido',
                      required: { value: true, message: 'Esse campo é obrigatório' },
                      maxLength: 15,
                    }}
                    labelClasses={styles.inputLabel}
                    showErrorMessage={!!errors.cep?.message}
                  />
                  <Link
                    href="https://buscacepinter.correios.com.br/app/endereco/index.php?t"
                    target="_blank"
                    style={{
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '4px',
                    }}
                  >
                    Não sei meu CEP{' '}
                    <ArrowForwardIosIcon style={{ fontSize: '12px', marginLeft: '4px' }} />
                  </Link>
                </div>

                <BasicInput
                  label="Endereço"
                  name="address"
                  required
                  disabled={!isEditing}
                  labelClasses={styles.inputLabel}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                    required: { value: true, message: 'Esse campo é obrigatório' },
                  }}
                />

                <BasicInput
                  label="Número"
                  name="number"
                  required
                  disabled={!isEditing}
                  labelClasses={styles.inputLabel}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                    required: { value: true, message: 'Esse campo é obrigatório' },
                  }}
                />

                <BasicInput
                  label="Complemento"
                  name="complement"
                  disabled={!isEditing}
                  labelClasses={styles.inputLabel}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                  }}
                />

                <Select
                  label="Estado"
                  name="state"
                  required
                  disabled={!isEditing}
                  error={!!errors?.state}
                  options={stateOptions}
                  labelClasses={styles.inputLabel}
                  getValue={item => item.value}
                  getLabel={item => item.label}
                  getKey={item => item.value}
                  menuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                  }}
                />

                <Select
                  label="Cidade"
                  name="city"
                  required
                  disabled={!isEditing}
                  error={!!errors?.city}
                  options={cityOptions}
                  labelClasses={styles.inputLabel}
                  getValue={item => item.label}
                  getLabel={item => item.label}
                  getKey={item => item.value}
                  menuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                  }}
                />

                <BasicInput
                  label="Bairro"
                  name="neighborhood"
                  required
                  disabled={!isEditing}
                  labelClasses={styles.inputLabel}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                    required: { value: true, message: 'Esse campo é obrigatório' },
                  }}
                />
              </div>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Regiões de atendimento</InputLabel>
              <div className={styles.wrapperFull}>
                <AddCity isProvider isEditing={isEditing} user={provider} worksInspection />
              </div>
            </div>
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  )
}
